import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { adminLoading, filePromises, handleError } from 'actions/admin';
import SimpleAdmin, { DefaultOptions } from 'components/simpleAdmin';
import CompetencyRatingSetter from './components/ratingSetter';
import ObjectiveManager from './components/objectiveManager';
import { hasModule } from 'helpers/profile';
import { setShowSuccess } from 'actions/ui';
import { apiGet, apiPost, apiPatch, apiDelete } from 'services';
import config from '../../../config';
import { SystemText, DebugInfo } from 'e4shared';

export const AdminPathways = ({ history }) => {
    const dispatch = useDispatch();
    const [showObjectiveManager, setShowObjectiveManager] = React.useState(false);
    const [currentPathway, setCurrentPathway] = React.useState({});
    const [competencies, setCompetencies] = React.useState([]);
    const [data, setData] = React.useState([]);

    const user = useSelector(state => state.user.user);

    React.useEffect(() => {
        apiGet('competency', user.guid, response => setCompetencies(response));
        api.load();
    }, []);

    React.useEffect(() => {
        DebugInfo('component', 'AdminPathways');
    }, []);

    const st = (path, ...rest) => SystemText(`Admin/Pathways/${path}`, ...rest);

    const api = {
        load: () => {
            dispatch(adminLoading(true));
            apiGet('pathway', user.guid, response => {
                setData(response);
                if (currentPathway?.id) {
                    const newPathway = response.find(d => d.id === currentPathway?.id);
                    if (newPathway) {
                        setCurrentPathway(newPathway);
                    } else {
                        setShowObjectiveManager(false);
                    }
                }

                dispatch(adminLoading(false));
            });
        },
        add: (pathway, callback) => {
            Promise.all(filePromises(pathway, ['badge_Icon'], user.guid, config)).then(() => {
                apiPost(
                    'pathway',
                    pathway,
                    user.guid,
                    () => {
                        dispatch(setShowSuccess(SystemText('Admin/Pathways/Success')));
                        callback();
                    },
                    error => {
                        handleError(dispatch, error);
                    }
                );
            });
        },
        update: (pathway, callback) => {
            Promise.all(filePromises(pathway, ['badge_Icon'], user.guid, config)).then(() => {
                apiPatch(
                    'pathway',
                    pathway,
                    user.guid,
                    () => {
                        dispatch(setShowSuccess(st('Success')));
                        callback();
                    },
                    error => {
                        handleError(dispatch, error);
                    }
                );
            });
        },
        remove: (ids, callback) => {
            if (!Array.isArray(ids)) ids = [ids];
            ids.forEach((id, idx) => {
                apiDelete(`pathway/${id}`, null, user.guid, () => {
                    if (idx + 1 === ids.length) {
                        callback?.();
                    }
                });
            });
        },
    };

    if (!user.sys_Config || !hasModule(user, 'pathways')) history.replace('/');

    const mappedCompetencies = competencies.filter(c => c.active).map(c => ({ ...c, name: c.admin_Full_Name }));

    const defaultValues = {
        active: true,
        competency_Values: '{}',
        due_Days: 30,
    };

    const options = {
        ...DefaultOptions(),
        label: st('Table/Pathway'),
    };

    let fields = [
        {
            label: st('Fields/Pathway/Label'),
            type: 'text',
            mandatory: true,
            dataProperty: 'name',
            maxLength: 100,
            generalErrors: true,
            uniqueErrors: true,
            help: st('Fields/Pathway/Help'),
            placeholder: st('Fields/Pathway/Placeholder'),
        },
        {
            label: st('Fields/Description/Label'),
            help: st('Fields/Description/Help'),
            type: 'textarea',
            dataProperty: 'description',
        },
        {
            label: st('Fields/MinRatings/Label'),
            type: 'select',
            dataProperty: 'competency_Ids',
            validation: entity => {
                const values = JSON.parse(entity.competency_Values);
                return entity.competency_Ids.some(c => !values[c]) ? st('Fields/MinRatings/Validation') : '';
            },
            data: mappedCompetencies,
            allowMultiple: true,
            placeholder: st('Fields/MinRatings/Placeholder'),
            help: st('Fields/MinRatings/Help'),
        },
        {
            type: 'special',
            dataProperty: 'competency_Values',
            renderer: (values, { value, onChange }) => (
                <CompetencyRatingSetter
                    competencies={mappedCompetencies}
                    competency_Ids={values.competency_Ids}
                    default_Competency_Values={value}
                    onChange={onChange}
                />
            ),
        },
        {
            label: st('Fields/Badge/Label'),
            type: 'toggle',
            dataProperty: 'badge_Show',
            help: st('Fields/Badge/Help'),
            inTitleBar: true,
        },
        {
            label: st('Fields/BadgeName/Label'),
            type: 'text',
            maxLength: 100,
            dataProperty: 'badge_Name',
            help: st('Fields/BadgeName/Help'),
            toggleProperty: 'badge_Show',
            placeholder: st('Fields/BadgeName/Placeholder'),
            mandatory: true,
            inTitleBar: true,
        },
        {
            label: st('Fields/BadgeDesc/Label'),
            type: 'text',
            maxLength: 100,
            dataProperty: 'badge_Description',
            help: st('Fields/BadgeDesc/Help'),
            toggleProperty: 'badge_Show',
            mandatory: true,
            inTitleBar: true,
        },
        {
            label: st('Fields/BadgeIcon/Label'),
            type: 'image',
            ratio: 1,
            dataProperty: 'badge_Icon',
            folder: 'badge_Icon',
            help: st('Fields/BadgeIcon/Help'),
            toggleProperty: 'badge_Show',
            inTitleBar: true,
        },
        {
            label: st('Fields/Manager/Label'),
            type: 'toggle',
            dataProperty: 'manager',
            help: st('Fields/Manager/Help'),
            inTitleBar: true,
        },
        {
            label: st('Fields/DisableRecommendations/Label'),
            type: 'toggle',
            dataProperty: 'disable_Recommendations',
            help: st('Fields/DisableRecommendations/Help'),
            inTitleBar: true,
        },
    ];

    if (!user.companySettings?.development?.usePerformanceDashboard) {
        fields = fields.filter(f => f.dataProperty !== 'disable_Recommendations');
    }

    const tableColumns = [
        { label: st('Table/Pathway'), dataProperty: 'name' },
        {
            label: st('Table/Competencies'),
            dataProperty: 'competency_Ids',
            showCount: true,
        },
        {
            label: st('Table/Objectives'),
            dataProperty: 'tasks',
            renderer: data => (
                <button
                    onClick={() => {
                        setCurrentPathway(data);
                        setShowObjectiveManager(true);
                    }}
                    className="btn btn-xs btn-info"
                >
                    {st('Table/Objectives/Manage', data.tasks?.length || 0)}
                </button>
            ),
        },
        {
            label: 'Manager',
            dataProperty: 'manager',
            toggle: true,
        },
    ];

    return (
        <div>
            <SimpleAdmin
                data={data}
                load={api.load}
                add={api.add}
                update={api.update}
                remove={api.remove}
                fields={fields}
                options={options}
                columns={tableColumns}
                defaultValues={defaultValues}
            />
            <ObjectiveManager
                show={showObjectiveManager}
                pathway={currentPathway}
                timestamp={new Date()}
                reload={api.load}
                close={() => setShowObjectiveManager(false)}
            />
        </div>
    );
};

export default AdminPathways;
